import { INLINE_ONLY_STEPS, NOTIFY_CUSTOM_LIMITS, STEP_ONLY_TRIGGER, SYSTEM_MESSAGE_TRIGGERS, WORKFLOW_P6_STEP_TYPES, type NotifyTarget, type WorkflowP6StepType, type WorkflowTrigger } from "@sagot/constants";
import type { NotifyRecipient, NotifyTeamStep, Step, SystemStep } from "../../types";
import { nameOf, type BuilderRefs } from "../refs";

/** What `t` of `useTranslation` is to these functions (tests pass a plain function). */
type TFunction = (key: string, options?: Record<string, unknown>) => string;

const S = "engage.workflows.system";
const P = "engage.workflows.summaries";
const SAMPLES = "engage.workflows.defaults.standard.samples";

const P6_TYPES: ReadonlySet<string> = new Set(WORKFLOW_P6_STEP_TYPES);

export const isSystemStep = (step: Step): step is SystemStep => P6_TYPES.has(step.type);

/** Whether a step type is one of the four phase 6 steps. */
export const isSystemType = (type: string): type is WorkflowP6StepType => P6_TYPES.has(type);

/** Whether today's code sends a standard message for the trigger (the registry has a builder for it). */
export const hasStandardMessage = (trigger: WorkflowTrigger | undefined): boolean => trigger !== undefined && SYSTEM_MESSAGE_TRIGGERS.has(trigger);

/**
 * Whether a step type can be added to a workflow on `trigger`: `notify_team` anywhere; the others only on the one trigger they
 * work on, and the two that decide at the call site only in a default (the copy of a default runs afterwards and uses Assign
 * or Update lead).
 */
export function systemStepOffered(type: WorkflowP6StepType, trigger: WorkflowTrigger, inDefault: boolean): boolean {
  const wanted = STEP_ONLY_TRIGGER[type];
  if (wanted !== undefined && wanted !== trigger) return false;
  return !INLINE_ONLY_STEPS.has(type) || inDefault;
}

/** A new step with the defaults a person starts from; the id comes from `freshStepId`. */
export function newSystemStep(type: WorkflowP6StepType, id: string): SystemStep {
  switch (type) {
    case "notify_team":
      return { id, type, category: "workflow", to: [{ target: "owners_admins" }], message: { template: "custom", title: "" } };
    case "route_handoff":
      return { id, type, mode: "agent_settings", round_robin: "team_setting" };
    case "assign_lead_owner":
      return { id, type, mode: "workspace_setting" };
    case "send_to_subscribed_endpoints":
      return { id, type };
  }
}

/** The title and body of the standard message of a trigger, as an example (a real notice has the visitor's own name). */
export function sampleMessage(trigger: WorkflowTrigger | undefined, t: TFunction): { title: string; body: string } | null {
  if (!hasStandardMessage(trigger)) return null;
  const title = t(`${SAMPLES}.${String(trigger)}.title`, { defaultValue: "" });
  return title ? { title, body: t(`${SAMPLES}.${String(trigger)}.body`, { defaultValue: "" }) } : null;
}

/** The step a "Write my own" starts: custom wording, filled with the example so nothing is typed from nothing. */
export function withOwnMessage(step: NotifyTeamStep, trigger: WorkflowTrigger | undefined, t: TFunction): NotifyTeamStep {
  const sample = sampleMessage(trigger, t);
  const title = (sample?.title ?? "").slice(0, NOTIFY_CUSTOM_LIMITS.title);
  const body = (sample?.body ?? "").slice(0, NOTIFY_CUSTOM_LIMITS.body);
  return { ...step, message: { template: "custom", title, ...(body ? { body } : {}) } };
}

export const needsTeam = (target: NotifyTarget): boolean => target === "team";
export const needsMember = (target: NotifyTarget): boolean => target === "user";
export const needsPermission = (target: NotifyTarget): boolean => target === "permission";

/** A recipient moved to another target keeps nothing that belonged to the old one (the server refuses an id the target does not take). */
export function retarget(recipient: NotifyRecipient, target: NotifyTarget, directOk: boolean): NotifyRecipient {
  const next: NotifyRecipient = { target };
  if (recipient.delivery === "direct" && directOk) next.delivery = "direct";
  return next;
}

/** What a recipient still needs before the step can work. */
function recipientMissing(recipient: NotifyRecipient): boolean {
  if (needsTeam(recipient.target)) return !recipient.team_id;
  if (needsMember(recipient.target)) return !recipient.user_id;
  return needsPermission(recipient.target) ? !recipient.permission : false;
}

/** What a system step still needs before it can work (the `builder.incomplete` key), or `null`. Only a hint on the card: the server checks the saved draft. */
export function systemIncompleteOf(step: SystemStep): string | null {
  switch (step.type) {
    case "notify_team":
      if (step.to.some(recipientMissing)) return "recipient";
      return step.message.template === "custom" && !step.message.title.trim() ? "notifyTitle" : null;
    case "route_handoff":
      if (step.mode === "team") return step.team_id ? null : "routeTeam";
      return step.mode === "user" && !step.user_id ? "routeUser" : null;
    case "assign_lead_owner":
      return step.mode === "user" && !step.user_id ? "ownerUser" : null;
    case "send_to_subscribed_endpoints":
      return null;
  }
}

function recipientName(recipient: NotifyRecipient, refs: BuilderRefs, t: TFunction): string {
  const unknown = t(`${P}.unknown`);
  if (recipient.target === "team") return t(`${S}.notify.teamNamed`, { name: nameOf(refs.teams, recipient.team_id, unknown) });
  if (recipient.target === "user") return nameOf(refs.members, recipient.user_id, unknown);
  if (recipient.target === "permission") return t(`${S}.notify.permissionNamed`, { permission: recipient.permission ?? unknown });
  return t(`${S}.notify.targets.${recipient.target}`);
}

/** One line that says what a system step does, shown on its card. */
export function summarizeSystem(step: SystemStep, refs: BuilderRefs, t: TFunction): string {
  const unknown = t(`${P}.unknown`);
  switch (step.type) {
    case "notify_team": {
      const who = step.to.map((recipient) => recipientName(recipient, refs, t)).join(", ");
      const what = step.message.template === "system" ? t(`${S}.notify.standardShort`) : step.message.title || unknown;
      return t(`${P}.notify_team`, { who, what });
    }
    case "route_handoff":
      if (step.mode === "team") return t(`${P}.route_team`, { name: nameOf(refs.teams, step.team_id, unknown) });
      return step.mode === "user" ? t(`${P}.route_user`, { name: nameOf(refs.members, step.user_id, unknown) }) : t(`${P}.route_agent_settings`);
    case "assign_lead_owner":
      return step.mode === "user" ? t(`${P}.owner_user`, { name: nameOf(refs.members, step.user_id, unknown) }) : t(`${P}.owner_${step.mode}`);
    case "send_to_subscribed_endpoints":
      return t(`${P}.send_to_subscribed_endpoints`);
  }
}
